import { Pencil } from 'lucide-react'
import { useT } from '@/i18n/index.ts'

/**
 * The selected agent's own starters, as a row of chips.
 *
 * Picking one **fills the composer and never sends it** (AGM-005). A starter that fired on a
 * single click would be an action taken with the reader's own authority before they had read
 * a word of it, and a deceptive starter is the abuse case that rule exists for. Identical
 * behaviour to the listing page, deliberately.
 *
 * An agent with no starters gets no row rather than a row of invented questions: a prompt the
 * agent cannot answer is worse than silence.
 */
export const StarterChips = ({
  starters,
  onPick
}: {
  starters: readonly string[]
  onPick: (prompt: string) => void
}) => {
  // Before the early return, because it is a hook — and in the body rather than at module
  // scope, because a captured pack renders the previous language after a switch (F-3).
  const t = useT().marketplace.gallery.about

  if (starters.length === 0) return null

  return (
    <ul
      aria-label={t.startersTitle}
      className="rise mt-5 flex flex-wrap justify-center gap-[7px]"
      style={{ animationDelay: '120ms' }}
    >
      {starters.map((prompt) => (
        <li key={prompt} className="flex min-w-0">
          <button
            type="button"
            onClick={() => onPick(prompt)}
            title={prompt}
            className="flex h-[33px] max-w-[320px] items-center gap-[7px] rounded-full border border-rule bg-card px-[13px] text-[12.5px] font-medium tracking-[-0.01em] text-fg-muted transition-[transform,box-shadow,color,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-rule-strong hover:text-fg hover:shadow-glass"
          >
            <Pencil size={13} strokeWidth={2} className="shrink-0 text-primary" aria-hidden />
            {/* Clipped rather than wrapped: a starter may be 200 characters, and the button's
                accessible name stays the whole question because CSS truncation is not the DOM. */}
            <span className="truncate">{prompt}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}
